import Button from '@components/Button';
import { CheckboxNoFunc } from '@components/Checkbox';
import { showConfirmModal } from '@components/ConfirmModal';
import { sendSuccess } from '@components/Noti';
import Tip from '@components/Tip';
import { TipWrapper } from '@components/TipWrapper';
import Title from '@components/Title';
import IconExit from '@components/icons/IconExit';
import { Menu } from '@headlessui/react';
import { showSceneModal } from '@modules/category';
import SidebarHeader from '@modules/sidebar/components/SidebarHeader';
import { $sceneMapApi } from '@stores/dataStores';
import {
  $componentLayerBatchSelected,
  $componentLayerBatchSelectedApi,
} from '@stores/editingComponentLayerStores';
import type { SceneData } from '@types';
import getSelectedStatus from '@utils/getSelectedStatus';
import { useStore } from 'effector-react';
import { type ReactNode, useEffect } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { deleteComponentLayers, onComponentLayerClick } from '../actions';
import { C_LAYER_DEFAULT, C_LAYER_MAX } from '../constants';
import { generateCLayerId } from '../utils/generateCLayerId';
import BatchModeItem from './BatchModeItem';

interface Props {
  scene: SceneData;
  onQuitClick: () => void;
}

export function ComponentLayerBatchModeList({ scene, onQuitClick }: Props) {
  const list = scene.config.components ?? [];
  const selected = useStore($componentLayerBatchSelected);

  useEffect(() => {
    const unwatch = onComponentLayerClick.watch(({ id }) => {
      $componentLayerBatchSelectedApi.select(id);
    });
    return () => {
      unwatch();
      $componentLayerBatchSelectedApi.clear();
    };
  }, []);

  const handleSelectAll = () => {
    if (selected.size < list.length) {
      $componentLayerBatchSelectedApi.set(new Set(list.map((x) => x.id)));
    } else {
      $componentLayerBatchSelectedApi.clear();
    }
  };

  const toggleValue = (key: 'locked' | 'visible', value: boolean) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.config.components?.forEach((x) => {
          if (selected.has(x.id)) {
            x[key] = value;
          }
        });
      },
    });
    if (key === 'locked') {
      sendSuccess(value ? '所选图层已锁定' : '所选图层已解锁');
    }
    if (key === 'visible') {
      sendSuccess(value ? '所选图层已显示' : '所选图层已隐藏');
    }
  };

  const handleCopyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要复制到的场景',
      disabledIds: [scene.id],
      multiple: true,
    });
    if (!idSet) return;
    const selectedList = list.filter((x) => selected.has(x.id));
    // 把所选图层复制到所选场景, 使用新生成的id, 并把layerControl重置为默认值
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        const sceneItem = draft.get(_id);
        if (!sceneItem) return;
        const newList = selectedList.map((component) => ({
          ...component,
          id: generateCLayerId(),
          layerControl: C_LAYER_DEFAULT.layerControl,
        }));
        const newComponents = [
          ...(sceneItem.config.components ?? []),
          ...newList,
        ];
        if (newComponents.length > C_LAYER_MAX) {
          newComponents.length = C_LAYER_MAX;
        }
        newComponents.forEach((x, i) => {
          x.zIndex = i;
        });
        sceneItem.config.components = newComponents;
      });
    });
    sendSuccess('图层复制到所选场景');
  };

  const handleDelete = async () => {
    const { confirmed } = await showConfirmModal({
      title: '操作确认',
      content: (
        <>
          <div className="mb-5 text-xl">{`确认删除选中的${selected.size}个图层吗？`}</div>
          <div className="text-center">删除后，图层不能被恢复。</div>
        </>
      ),
    });
    if (!confirmed) return;
    deleteComponentLayers({
      ids: selected,
      sceneId: scene.id,
    });
    $componentLayerBatchSelectedApi.clear();
  };

  return (
    <>
      <SidebarHeader
        title={
          <div className="flex">
            <Title>图层</Title>
            <Tip className="ml-10 self-end text-base">批量操作</Tip>
          </div>
        }
        btns={
          <TipWrapper text="退出操作" placement="bottom" size="sm">
            <IconExit className="focus:outline-none" onClick={onQuitClick} />
          </TipWrapper>
        }
      />
      <div
        className="mb-10 flex h-35 cursor-pointer items-center rounded bg-black-#34 px-10"
        onClick={handleSelectAll}
      >
        <CheckboxNoFunc
          status={getSelectedStatus(selected.size, list.length)}
        />
        <div className="ml-5">全选</div>
        <div className="ml-auto text-sm text-black-gray ">{`${selected.size}/${list.length}`}</div>
      </div>
      {list.length > 0 ? (
        <div className="relative flex-1 select-none">
          <Scrollbars>
            {list.map((x) => (
              <BatchModeItem
                key={x.id}
                data={x}
                isSelected={selected.has(x.id)}
                onClick={() => $componentLayerBatchSelectedApi.select(x.id)}
              />
            ))}
          </Scrollbars>
        </div>
      ) : (
        <Tip className="mt-5 flex-1 text-center text-base">
          没有图层可批量操作
        </Tip>
      )}
      <Menu as="div" className="relative mt-10">
        <Menu.Button as={Button} disabled={!selected.size} className="w-full">
          进行批量操作
        </Menu.Button>
        <Menu.Items className="absolute bottom-35 z-50 w-full pt-5">
          <div className="overflow-hidden rounded bg-white text-black focus:outline-none">
            <Item onClick={() => toggleValue('locked', true)}>锁定图层</Item>
            <Item onClick={() => toggleValue('locked', false)}>解锁图层</Item>
            <Item onClick={() => toggleValue('visible', false)}>隐藏图层</Item>
            <Item onClick={() => toggleValue('visible', true)}>显示图层</Item>
            <Item onClick={handleCopyTo}>图层复制到...</Item>
            <Item onClick={handleDelete}>删除图层</Item>
          </div>
        </Menu.Items>
      </Menu>
      <Button scheme="darkest" className="mt-10 w-full" onClick={onQuitClick}>
        退出
      </Button>
    </>
  );
}

function Item({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <Menu.Item>
      <div
        className="flex h-30 cursor-pointer items-center justify-center px-10 hover:bg-white-light"
        onClick={onClick}
      >
        {children}
      </div>
    </Menu.Item>
  );
}

export default ComponentLayerBatchModeList;
